import { formatPrivateInfo } from '@/features/activities/pages/Detail/ReflectionList/utils'
import { MetaDataType } from '@/uikit'
import { createEnumHelper, pick } from '@seiue/util'
import { UploadFile } from 'antd'
import { RuleObject } from 'antd/es/form'
import {
  FieldDomainEnum,
  ReflectionIdcardTypeEnum,
  ReflectionPupilTypeEnum,
} from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'
import {
  IFormItemTypeEnum,
  customizeCountValidator,
  genderToLabel,
  guardianTypeToLabel,
  idcardTypeToLabel,
  rulesMap,
  statusToLabel,
} from 'packages/utils'

import { IConfig, ReflectionWithInformId } from './types'

/**
 * 获取 extra 信息
 *
 * @param type - 组件类型
 * @param dataType - 相关限制
 * @returns 提示文案
 */
export const getExtraInfo = (
  type: IFormItemTypeEnum,
  dataType: MetaDataType
) => {
  const { minCount, maxCount, fileSizeLimit } = dataType
  if (type === 'multi' && (minCount || maxCount)) {
    return `至少选${minCount}项,至多选${maxCount}项`
  }
  if (type === 'filelist' && fileSizeLimit) {
    return `${minCount ? `最少传${minCount}个,` : ''}${
      maxCount ? `最多传${maxCount}个,` : ''
    }单个文件大小不超过${fileSizeLimit}M`
  }
  return undefined
}

/**
 * 根据组件类型选择FormItem的valuePropName属性
 *
 * @param type - 组件类型
 * @returns valuePropName
 */
export const selectValuePropsName = (type: IFormItemTypeEnum) => {
  if (type === IFormItemTypeEnum.Filelist) {
    return 'fileList'
  }
  return undefined
}

/**
 * 用户 domain 定义
 *
 * @returns 返回枚举类型
 */
export const domainToUseParamType = createEnumHelper(() => ({
  [FieldDomainEnum.FormalStudent]: '',
  [FieldDomainEnum.FormalTeacher]: '',
  [FieldDomainEnum.InformalStudent]: 'AdminBiosourceDetails',
  [FieldDomainEnum.InformalTeacher]: 'AdminTalentDetail',
}))

export const idCardFormConfig: IConfig[] = [
  {
    type: IFormItemTypeEnum.Select,
    label: '证件类型',
    name: 'idcardType',
    options: idcardTypeToLabel.listOptions('value', 'label'),
  },
  {
    type: IFormItemTypeEnum.Input,
    label: '证件号',
    name: 'idcard',
  },
]

/**
 * 设置选项标签
 *
 * @param fieldName - 字段名
 * @param name - 选项名
 * @returns 选择标签
 */
export const getOptionLabel = (fieldName: string, name: string) => {
  if (/^(guardians.0.pupil_type|guardians.1.pupil_type)$/.test(fieldName)) {
    return guardianTypeToLabel.get(name as ReflectionPupilTypeEnum)
  }
  if (/^idcard_type$/.test(fieldName)) {
    return idcardTypeToLabel.get(name as ReflectionIdcardTypeEnum)
  }
  return name
}

/**
 * 获取初始信息
 *
 * @param data - 数据
 * @returns 初始数据
 */
export const getInitFixedInfo = (data: ReflectionWithInformId) => {
  const fixedInfo = pick(data, [
    'name',
    'gender',
    'graduate',
    'registedAt',
    'status',
    'phone',
    'avatar',
    'archivedAt',
    'admissionedAt',
    'subject',
  ])
  return {
    ...fixedInfo,
    // TODO 临时隐藏
    // admissionedAt: fixedInfo.admissionedAt?.slice(0, 10),
    // archivedAt: fixedInfo.archivedAt?.slice(0, 10),
  }
}

/**
 * 获取显示到页面中的文本（展示状态下）
 *
 * @param name - 表单项name
 * @param value - 表单项初始值
 * @returns 显示到页面中的文本
 */
export const getShowData = (name: string, value: AnyType) => {
  switch (name) {
    case 'gender':
      return genderToLabel.get(value)
    case 'status':
      return statusToLabel.get(value)
    case 'admissionedAt':
    case 'archivedAt':
    case 'registedAt':
      return value ? value.slice(0, 16) : undefined
    case 'phone':
      return formatPrivateInfo(value, false)
    default:
      return value
  }
}

/**
 * 处理文件
 *
 * @param info - 文件数据
 * @returns 处理文件
 */
export const normFile = (info: AnyType) => {
  const list = info.fileList || []
  const pattern = /\?t=[0-9]*$/
  return list
    .filter((file: UploadFile) => file.url)
    .map((file: UploadFile) => {
      let fileUrl = file.url
      if (fileUrl) {
        fileUrl = pattern.test(fileUrl)
          ? fileUrl.replace(pattern, `?t=${+new Date()}`)
          : `${fileUrl}?t=${+new Date()}`
      }
      return {
        uid: file.uid,
        name: file.name,
        url: fileUrl,
        type: file.type,
      }
    })
}

/**
 * 获取 itemRender 规则
 *
 * @param isLimit - 是否限制
 * @param i - 表单配置项
 * @returns 规则
 */
export const getItemRenderRules = (isLimit: number | undefined, i: IConfig) => {
  const { minCount, maxCount } = i

  return isLimit
    ? [
        {
          validator: (
            rule: RuleObject,
            value: AnyType,
            callback: (error?: string | undefined) => void
          ) =>
            customizeCountValidator(rule, value, callback, {
              max: maxCount,
              min: minCount,
              type: i.type,
            }),
        },
        ...(i.rules ? i.rules : []),
      ]
    : [...(rulesMap[i.name] || []), ...(i.rules ? i.rules : [])]
}
